// ANGEL FLIGHT — InputSystem: drag-to-aim + hold-to-fire (DOM pointer events).
//
// Your finger IS the reticle. Touch anywhere on the sensor and the reticle eases
// to that point; hold past the spin-up and the gun roars; a quick tap is one
// shot. The reticle is clamped to the aimable window. Desktop mouse mirrors it.
// Sets intent on the gunner (aim, firing, spinup); FiringSystem turns intent
// into shots; the HUD draws the reticle from `gunner.aim`.
//
// Engine-agnostic: binds raw DOM pointer events to `view.domElement`. In headless
// tests `view.domElement` is null, so binding is skipped and the sim is driven by
// directly setting gunner intent.

import { BALANCE } from '../config/balance';
import { WEAPON_SLOTS } from '../config/weapons';
import { clamp, damp } from '../core/mathx';
import type { Vec2, WeaponId } from '../core/types';
import type { GameContext, GameSystem } from './System';

/** Weapon-switch hotkeys → the weapon they select (keyboard 1-4), derived from
 *  the shared WEAPON_SLOTS order so keys, touch buttons, and the HUD all agree.
 *  Switching is gated by ctx.ownedWeapons — an unowned slot is a LOCKED no-op
 *  (the Armory sells the rest; the HUD shows the honest lock states). */
const KEY_TO_WEAPON: Readonly<Record<string, WeaponId>> = Object.freeze(
  Object.fromEntries(WEAPON_SLOTS.map((w, i) => [`Digit${i + 1}`, w])),
);

export class InputSystem implements GameSystem {
  private ctx!: GameContext;
  private el: HTMLCanvasElement | null = null;
  // Reticle aim lives in the logical 1280×720 view space (BALANCE.VIEW), so the
  // existing aim-window clamps + smoothing constants carry over unchanged.
  private targetX: number = BALANCE.gun.muzzleX;
  private targetY: number = BALANCE.VIEW.height * 0.42;
  private reticleX: number = this.targetX;
  private reticleY: number = this.targetY;
  private held = false;
  private heldMs = 0;
  /** Pending one-shot from a quick tap, consumed on the next update. */
  private tapShotMs = 0;

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.el = ctx.view.domElement;
    if (!this.el) return; // headless: no DOM to bind
    this.el.addEventListener('pointerdown', this.onDown);
    this.el.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    window.addEventListener('keydown', this.onKey);
  }

  /** Keyboard weapon-switch (1-4). Headless tests drive it via switchWeapon().
   *  No-op if the chosen weapon is already active or NOT OWNED (the lock is
   *  honest — buying it in the Armory is the only way in). */
  switchWeapon(weapon: WeaponId): void {
    const g = this.ctx.gunner;
    if (!g || g.activeWeapon === weapon) return;
    if (!this.ctx.ownedWeapons.includes(weapon)) return;
    g.activeWeapon = weapon;
    this.ctx.bus.emit('weapon:switched', { weapon });
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    const weapon = KEY_TO_WEAPON[e.code];
    if (weapon) this.switchWeapon(weapon);
    if (e.code === 'KeyF') this.deployFlares();
  };

  /** Pop countermeasure flares (key F). Headless tests call this directly.
   *  A no-op while on cooldown — the HUD shows the honest readiness state. */
  deployFlares(): void {
    const g = this.ctx.gunner;
    if (!g || !g.deployFlares()) return;
    this.ctx.bus.emit('flare:deployed', {
      activeMs: BALANCE.flares.activeMs,
      cooldownMs: BALANCE.flares.cooldownMs,
    });
  }

  /** Map a DOM client point to the logical 1280×720 view space. */
  private toView(clientX: number, clientY: number): Vec2 {
    const el = this.el;
    if (!el) return { x: this.targetX, y: this.targetY };
    const r = el.getBoundingClientRect();
    const nx = (clientX - r.left) / Math.max(1, r.width);
    const ny = (clientY - r.top) / Math.max(1, r.height);
    return { x: nx * BALANCE.VIEW.width, y: ny * BALANCE.VIEW.height };
  }

  private clampAim(x: number, y: number): void {
    const cx = BALANCE.VIEW.width / 2;
    this.targetX = clamp(x, cx - BALANCE.gun.aimRangeX, cx + BALANCE.gun.aimRangeX);
    const top = BALANCE.VIEW.height * 0.18;
    this.targetY = clamp(y, top, top + BALANCE.gun.aimRangeY * 2);
  }

  private readonly onDown = (e: PointerEvent): void => {
    this.held = true;
    this.heldMs = 0;
    const v = this.toView(e.clientX, e.clientY);
    this.clampAim(v.x, v.y);
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (!this.held) return;
    const v = this.toView(e.clientX, e.clientY);
    this.clampAim(v.x, v.y);
  };

  private readonly onUp = (): void => {
    // quick tap → one shot (consumed over a short window in update())
    if (this.held && this.heldMs < BALANCE.gun.tapMs) {
      this.tapShotMs = 30;
    }
    this.held = false;
    this.heldMs = 0;
    if (this.ctx.gunner) this.ctx.gunner.spinup = 0;
  };

  update(dt: number): void {
    const g = this.ctx.gunner;
    if (!g) return;
    const dtSec = dt / 1000;

    this.reticleX = damp(this.reticleX, this.targetX, BALANCE.gun.smoothLambda, dtSec);
    this.reticleY = damp(this.reticleY, this.targetY, BALANCE.gun.smoothLambda, dtSec);
    g.aim = { x: this.reticleX, y: this.reticleY };

    if (this.held) {
      this.heldMs += dt;
      const t = Math.min(1, this.heldMs / BALANCE.gun.spinupMs);
      g.spinup = t;
      g.firing = this.heldMs >= BALANCE.gun.tapMs;
    } else if (this.tapShotMs > 0) {
      // one-shot tap: fire briefly then release
      g.firing = true;
      g.spinup = 1;
      this.tapShotMs -= dt;
      if (this.tapShotMs <= 0) {
        g.firing = false;
        g.spinup = 0;
      }
    }
    // Emit EVERY frame, not just while held: the audio whine tracks this value,
    // so a release must drive it back to 0 — emitting only on hold left the
    // turbine osc frozen at its last pitch/level (a permanent screech).
    this.ctx.bus.emit('weapon:spinup', { weapon: g.activeWeapon, t: g.spinup });
  }

  destroy(): void {
    if (!this.el) return;
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    window.removeEventListener('keydown', this.onKey);
  }
}
